import { useGetAllBackupVaultsWithItems } from "../../hooks/useBackup";
import { MdOutlineBackup, MdStorage, MdComputer, MdInbox, MdCheckCircle, MdError, MdSchedule } from "react-icons/md";

const BackupPage = () => {
  const { data: backupVaults, isLoading, error, refetch } = useGetAllBackupVaultsWithItems();

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "Never";
    const date = new Date(dateTimeStr);
    return date.toLocaleString();
  };

  const getBackupStatusBadge = (status) => {
    if (status === "Completed") {
      return (
        <span className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-green-500/20 text-green-400 border border-green-500">
          <MdCheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden xs:inline">Success</span>
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-red-500/20 text-red-400 border border-red-500">
        <MdError className="w-3 h-3 sm:w-4 sm:h-4" />
        <span className="hidden xs:inline">Failed</span>
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="px-3 sm:px-6 md:px-10 lg:px-16 py-4 sm:py-8 md:py-10">
        <div className="bg-primary-dark rounded-2xl p-6 sm:p-10">
          <div className="text-center py-8 sm:py-16">
            <div className="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-4 sm:mt-5 text-base sm:text-lg">Loading backup vaults...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-3 sm:px-6 md:px-10 lg:px-16 py-4 sm:py-8 md:py-10">
        <div className="bg-red-500/20 border border-red-500 rounded-2xl p-6 sm:p-10 text-center">
          <p className="text-red-400 text-base sm:text-lg">Error: {error.message}</p>
          <button
            onClick={() => refetch()}
            className="mt-4 sm:mt-6 px-4 sm:px-6 py-2 sm:py-2.5 bg-primary-cyan text-white rounded-lg hover:bg-primary-cyan/80 transition text-sm sm:text-base font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 md:px-6 py-3 sm:py-4">
      <div className="space-y-6 sm:space-y-8 md:space-y-10">
        {backupVaults?.map((vault) => (
          <div key={vault.vaultName} className="bg-primary-dark rounded-2xl border border-gray-700 hover:border-primary-cyan/50 transition-all duration-300 overflow-hidden">
            <div className="p-4 sm:p-6 md:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
                <MdOutlineBackup className="text-primary-cyan text-3xl sm:text-4xl" />
                <div>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white break-words">{vault.vaultName}</h2>
                  <span className="px-2 sm:px-3 py-1 sm:py-1.5 bg-primary-cyan/20 text-primary-cyan rounded-lg text-xs sm:text-sm font-medium inline-block mt-2">
                    {vault.storageType}
                  </span>
                </div>
              </div>

              <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-700">
                <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <MdStorage className="text-primary-cyan text-xl sm:text-2xl" />
                  Protected Items
                </h3>
                
                {vault.protectedItems && vault.protectedItems.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                    {vault.protectedItems.map((item, idx) => (
                      <div key={idx} className="bg-gray-800/50 rounded-xl p-4 sm:p-6 border border-gray-700 hover:border-primary-cyan/30 transition">
                        <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-5">
                          <MdComputer className="text-primary-cyan text-xl sm:text-2xl flex-shrink-0 mt-1" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-base sm:text-lg font-semibold truncate">{item.vmName || "Unknown VM"}</p>
                            <p className="text-gray-400 text-xs sm:text-sm break-words mt-1">{item.dataSourceName}</p>
                          </div>
                        </div>

                        <div className="space-y-3 sm:space-y-4">
                          <div className="bg-gray-900/50 rounded-lg p-3 sm:p-4">
                            <div className="flex items-center gap-2 mb-2 sm:mb-3">
                              <MdSchedule className="text-primary-cyan w-4 h-4 sm:w-5 sm:h-5" />
                              <span className="text-gray-300 text-xs sm:text-sm font-medium">Last Backup</span>
                            </div>
                            {item.lastBackup ? (
                              <div className="space-y-2">
                                <div className="flex flex-wrap justify-between items-center gap-2">
                                  <span className="text-gray-400 text-xs sm:text-sm">Status</span>
                                  {getBackupStatusBadge(item.lastBackup.status)}
                                </div>
                                <div className="flex flex-wrap justify-between items-center gap-2">
                                  <span className="text-gray-400 text-xs sm:text-sm">Time</span>
                                  <span className="text-white text-xs sm:text-sm break-all text-right">{formatDateTime(item.lastBackup.startTime)}</span>
                                </div>
                                {item.lastBackup.duration && (
                                  <div className="flex flex-wrap justify-between items-center gap-2">
                                    <span className="text-gray-400 text-xs sm:text-sm">Duration</span>
                                    <span className="text-white text-xs sm:text-sm">{item.lastBackup.duration}</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-xs sm:text-sm text-center py-3">No backup history</p>
                            )}
                          </div>

                          <div className="bg-gray-900/50 rounded-lg p-3 sm:p-4">
                            <div className="flex items-center gap-2 mb-2 sm:mb-3">
                              <MdSchedule className="text-primary-cyan w-4 h-4 sm:w-5 sm:h-5" />
                              <span className="text-gray-300 text-xs sm:text-sm font-medium">30 Days Ago</span>
                            </div>
                            {item.backup30DaysAgo ? (
                              <div className="space-y-2">
                                <div className="flex flex-wrap justify-between items-center gap-2">
                                  <span className="text-gray-400 text-xs sm:text-sm">Status</span>
                                  {getBackupStatusBadge(item.backup30DaysAgo.status)}
                                </div>
                                <div className="flex flex-wrap justify-between items-center gap-2">
                                  <span className="text-gray-400 text-xs sm:text-sm">Time</span>
                                  <span className="text-white text-xs sm:text-sm break-all text-right">{formatDateTime(item.backup30DaysAgo.startTime)}</span>
                                </div>
                                {item.backup30DaysAgo.duration && (
                                  <div className="flex flex-wrap justify-between items-center gap-2">
                                    <span className="text-gray-400 text-xs sm:text-sm">Duration</span>
                                    <span className="text-white text-xs sm:text-sm">{item.backup30DaysAgo.duration}</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-xs sm:text-sm text-center py-3">No backup data</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 sm:py-16 bg-gray-800/30 rounded-xl">
                    <MdInbox className="text-gray-500 w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-4" />
                    <p className="text-gray-400 text-base sm:text-lg">No protected items in this vault</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {(!backupVaults || backupVaults.length === 0) && (
          <div className="bg-primary-dark rounded-2xl p-12 sm:p-20 text-center border border-gray-700">
            <MdInbox className="text-gray-500 w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-5" />
            <p className="text-gray-400 text-lg sm:text-xl">No backup vaults found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BackupPage;